import React, { useState, useEffect } from 'react';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Divider, Box, useMediaQuery, useTheme, Collapse, IconButton } from '@mui/material';
import { Typography } from '@mui/material';
import { 
  Dashboard, 
  Analytics, 
  AutoFixHigh,
  Map, 
  DataObject, 
  Assessment, 
  Chat, 
  Help, 
  Settings,
  TrendingUp,
  Store,
  Public,
  Home,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings
} from '@mui/icons-material';
//import { Chat, Category, Store, PieChart, LocalMall } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import logo from './logo_unofficial.png';
import { API_URL } from './constants';
import { PRODUCT_VIEW_ROUTE_SEGMENT, DISTRIBUTION_VIEW_ROUTE_SEGMENT } from './productViewProjectConfig';
import { useUserModules } from './UserModulesContext';
import { useAuth } from './AuthContext';
import axios from 'axios';

const drawerWidth = 300;

const PRICE_MONITORING_NAV_IDS = ['bat-product-products', 'bat-product-brands', 'bat-product-retailers'];
const DISTRIBUTION_VIEW_NAV_IDS = ['distribution_view-brands', 'distribution_view-retailers'];

const isPriceMonitoringNavId = (id) => PRICE_MONITORING_NAV_IDS.includes(id);
const isDistributionViewNavId = (id) => DISTRIBUTION_VIEW_NAV_IDS.includes(id);

/** Shown after the Price Monitoring / Distribution View collapsibles (TOC view, then Price Effects when present). */
const AFTER_PRICE_MONITORING_NAV_IDS = ['tocview', 'bat-effects'];

const afterPriceMonitoringOrderIndex = (id) => {
  const idx = AFTER_PRICE_MONITORING_NAV_IDS.indexOf(id);
  return idx === -1 ? -1 : idx;
};

const Sidebar = ({ isLoggedIn, projectKey, username, scopeMode, uiStyle }) => {
  //if (!isLoggedIn) return null;

  const [apiVersion, setApiVersion] = useState('v1.0.0'); // Default fallback
  const [appVersion, setAppVersion] = useState('-');
  const [appVersionDate, setAppVersionDate] = useState('-');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { enabledModules, loadUserModules, isModuleEnabled, reloadModules } = useUserModules();
  const { isAdmin, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({});
  const [priceMonitoringExpanded, setPriceMonitoringExpanded] = useState(true);
  const [distributionViewExpanded, setDistributionViewExpanded] = useState(true);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await axios.get(`${API_URL}/version`);
        setApiVersion(response.data.version);
      } catch (error) {
        console.warn('Failed to fetch API version:', error);
        // Keep the default version if API call fails
      }
    };

    const fetchAppVersion = async () => {
      try {
        const response = await axios.get(`${API_URL}/app-version`);
        setAppVersion(response.data.version || '-');
        setAppVersionDate(response.data.date || '-');
      } catch (error) {
        console.warn('Failed to fetch app version:', error);
        // Keep the default version if API call fails
      }
    };

    fetchVersion();
    fetchAppVersion();
  }, []); // Only run once on mount

  useEffect(() => {
    if (username && projectKey) {
      loadUserModules(username, projectKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, projectKey]); // Only depend on username and projectKey, not loadUserModules

  // Force sidebar to re-render when enabledModules changes
  // Also reload modules periodically to catch updates from other tabs/sessions
  useEffect(() => {
    // This effect ensures the sidebar updates when modules change
    console.log('Sidebar: enabledModules changed', enabledModules);
    
    // Reload modules every 30 seconds to catch updates
    const interval = setInterval(() => {
      if (username && projectKey) {
        reloadModules(username, projectKey);
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [enabledModules, username, projectKey, reloadModules]);


  const basePath = projectKey ? `/${projectKey}` : '';

  // Define all possible navigation items with icons and grouping
  const allNavItems = [
    // Analytics Group
    { id: 'powerbi', label: 'PowerBI Dashboard', icon: <Dashboard />, path: `${basePath}/powerbi`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Analytics' },
    { id: 'brand-board', label: 'Brand Board', icon: <Store />, path: `${basePath}/brand-board`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'brand-board-summary', label: 'Brand Board Summary', icon: <Store />, path: `${basePath}/brand-board-summary`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'brand-board-summary', label: 'Brand Board Summary (Styled)', icon: <Store />, path: `${basePath}/brand-board-summary-styled`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'brand-board-summary-palette', label: 'Brand Board Summary', icon: <Store />, path: `${basePath}/brand-board-summary-palette`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'jti-product', label: 'Price and Promotions', icon: <Store />, path: `${basePath}/jti-product`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Analytics' },
    { id: 'product-multi-country', label: 'Multiple Countries P-index', icon: <Public />, path: `${basePath}/product-multi-country`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    // TOC Group
    { id: 'tocview', label: 'Total Ownership View', icon: <Assessment />, path: `${basePath}/tocview`, badge: { text: 'beta', color: '#f5a057' }, group: 'Analytics' },
    
    // Lindt-only analytics
    { id: 'product', label: 'Product View', icon: <Analytics />, path: `${basePath}/product`, badge: { text: 'beta', color: '#f5a057' }, group: 'Analytics' },
    { id: 'retailer', label: 'Retailer View', icon: <Store />, path: `${basePath}/retailer`, badge: { text: 'beta', color: '#f5a057' }, group: 'Analytics' },
    { id: 'distribution_brand', label: 'Brand Distribution', icon: <Assessment />, path: `${basePath}/distribution_brand`, badge: { text: 'beta', color: '#f5a057' }, group: 'Analytics' },
    { id: 'distribution_retailer', label: 'Retailer Distribution', icon: <DataObject />, path: `${basePath}/distribution_retailer`, badge: { text: 'beta', color: '#f5a057' }, group: 'Analytics' },

    // Banners Group
    { id: 'product-availability', label: 'Product Availability', icon: <Analytics />, path: `${basePath}/product-availability`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'banner-evolution', label: 'Banner Evolution', icon: <TrendingUp />, path: `${basePath}/banner-evolution`, badge: { text: 'new', color: '#4682B4' }, group: 'Banners' },
    { id: 'worldmap', label: 'World Map', icon: <Public />, path: `${basePath}/worldmap`, badge: { text: 'beta', color: '#f5a057' }, group: 'Banners' },
    { id: 'worldmap', label: 'World Map (Green)', icon: <Map />, path: `${basePath}/worldmap-green`, badge: { text: 'new', color: '#2e7d32' }, group: 'Banners' },
    
    // AI Group
    { id: 'chat', label: 'Chat View', icon: <Chat />, path: `${basePath}/chat`, badge: { text: 'beta', color: '#f5a057' }, group: 'AI' },
    
    // Data Group
    { id: 'product-detail', label: 'Product Detail View', icon: <DataObject />, path: `${basePath}/product-detail`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Data' },
    { id: 'jti-products-overview', label: 'Products Mapping Overview', icon: <DataObject />, path: `${basePath}/jti-products-overview`, badge: { text: 'new', color: '#4682B4' }, group: 'Data' },
    { id: 'product-matching', label: 'Matching', icon: <Assessment />, path: `${basePath}/product-matching`, badge: { text: 'new', color: '#4682B4' }, group: 'Data' },
    { id: 'jti-product-matching', label: 'Product Matching', icon: <Assessment />, path: `${basePath}/jti-product-matching`, badge: { text: 'new', color: '#4682B4' }, group: 'Data' },
    { id: 'product-detail-unified', label: 'Product Detail View', icon: <DataObject />, path: `${basePath}/product-detail-unified`, badge: { text: 'beta', color: '#f5a057' }, group: 'Data' },
    { id: 'data-sources', label: 'Data Sources Overview', icon: <DataObject />, path: `${basePath}/data-sources`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Data' },
    { id: 'bat-product-products', label: 'By Products', icon: <Analytics />, path: `${basePath}/${PRODUCT_VIEW_ROUTE_SEGMENT}/products`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'bat-product-brands', label: 'By Brands', icon: <Analytics />, path: `${basePath}/${PRODUCT_VIEW_ROUTE_SEGMENT}/brands`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'bat-product-retailers', label: 'By Retailers', icon: <Analytics />, path: `${basePath}/${PRODUCT_VIEW_ROUTE_SEGMENT}/retailers`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'distribution_view-brands', label: 'By Brands', icon: <Assessment />, path: `${basePath}/${DISTRIBUTION_VIEW_ROUTE_SEGMENT}/brands`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'distribution_view-retailers', label: 'By Retailers', icon: <Assessment />, path: `${basePath}/${DISTRIBUTION_VIEW_ROUTE_SEGMENT}/retailers`, badge: { text: 'new', color: '#4682B4' }, group: 'Analytics' },
    { id: 'bat-effects', label: 'Price Effects', icon: <AutoFixHigh />, path: `${basePath}/bat-effects`, badge: { text: 'AI', color: '#7b1fa2' }, group: 'Analytics' },
    { id: 'bat-product-matching', label: 'Product Matching', icon: <Assessment />, path: `${basePath}/bat-product-matching`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Data' },
    { id: 'reference-catalog', label: 'Reference Catalog', icon: <DataObject />, path: `${basePath}/reference-catalog`, badge: { text: 'new', color: '#4682B4' }, group: 'Data', nested: true },
    { id: 'export-pricing-data', label: 'Export pricing data', icon: <DataObject />, path: `${basePath}/export-pricing-data`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Data' },
    { id: 'sweden-snus-admin', label: 'Data & shelf health', icon: <DataObject />, path: `${basePath}/data-insights`, badge: { text: 'GTM', color: '#0f6b4c' }, group: 'Data' },
    { id: 'sweden-snus-reports', label: 'Shelf reports', icon: <Public />, path: `${basePath}/shelf-reports`, badge: { text: 'share', color: '#0f6b4c' }, group: 'Data' },
    { id: 'jti-export-data', label: 'Exports & Reports', icon: <DataObject />, path: `${basePath}/jti-export-data`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Data' },
    { id: 'jti-embedded-app', label: 'Mirror websites', icon: <Public />, path: `${basePath}/jti-embedded-app`, badge: { text: 'new', color: '#4682B4' }, group: 'Data' },
    { id: 'product-categorisation', label: 'Product Categorisation Definitions', icon: <Assessment />, path: `${basePath}/product-categorisation`, badge: { text: 'new', color: '#4682B4' }, group: 'Data' },
    { id: 'pricing-types', label: 'Pricing Types Definitions', icon: <Assessment />, path: `${basePath}/pricing-types`, badge: { text: 'new', color: '#4682B4' }, group: 'Data' },
    { id: 'real_estate', label: 'Real Estate', icon: <Home />, path: `${basePath}/real_estate`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Data' },
    { id: 'real_estate_map', label: 'Real Estate Map', icon: <Public />, path: `${basePath}/real_estate/map`, badge: { text: 'new', color: '#4682B4' }, group: 'Data' },
    
    
    // Support & Settings Group
    { id: 'role-management', label: 'Role Management', icon: <AdminPanelSettings />, path: `${basePath}/role-management`, badge: { text: 'superadmin', color: '#d32f2f' }, group: 'Support' },
    { id: 'support', label: 'Support', icon: <Help />, path: `${basePath}/support`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Support' },
    { id: 'settings', label: 'Settings', icon: <Settings />, path: `${basePath}/settings`, badge: { text: 'ready', color: '#2c5f20' }, group: 'Support' },
  ];

  const isJtiGlobal = projectKey === 'jti-global';
  const isJtiScopeProject = ['jti-global', 'jti'].includes(projectKey);
  const isBatProject = ['bat', 'pmi'].includes(projectKey);
  const isPmiProject = projectKey === 'pmi';
  const normalizedScope = ['hq', 'market', 'old'].includes(scopeMode) ? scopeMode : 'market';
  const isWhiteLabel = uiStyle === 'white-label';
  
  // BAT / PMI white-label color palettes
  const batPrimary = '#24336B';
  const batLight = '#e8ecf5';
  const batLighter = '#f0f4f9';
  const batBorder = '#c4d1e8';
  const batText = '#1a2540';
  const batAccent = '#3d4f7a';
  const pmiPrimary = 'rgb(0, 116, 194)';
  const pmiLight = '#e6f2fb';
  const pmiLighter = '#f2f8fe';
  const pmiBorder = '#bfdcf4';
  const pmiText = '#0f3f66';
  const pmiAccent = '#0b5f9a';
  
  // Determine color scheme based on project
  const isBatWhiteLabel = isWhiteLabel && isBatProject;
  const isJtiWhiteLabel = isWhiteLabel && !isBatProject;
  const clientPrimary = isPmiProject ? pmiPrimary : batPrimary;
  const clientLight = isPmiProject ? pmiLight : batLight;
  const clientLighter = isPmiProject ? pmiLighter : batLighter;
  const clientBorder = isPmiProject ? pmiBorder : batBorder;
  const clientText = isPmiProject ? pmiText : batText;
  const clientAccent = isPmiProject ? pmiAccent : batAccent;
  const clientActiveBackground = isPmiProject ? 'rgba(0, 116, 194, 0.14)' : 'rgba(36, 51, 107, 0.14)';
  const clientHoverBackground = isPmiProject ? 'rgba(0, 116, 194, 0.12)' : 'rgba(36, 51, 107, 0.12)';
  
  const sidebarBackground = isBatWhiteLabel ? clientLighter : (isJtiWhiteLabel ? '#f6fbf8' : '#f0f1f2');
  const sidebarBorder = isBatWhiteLabel ? clientBorder : (isJtiWhiteLabel ? '#d9e3dd' : '#e0e0e0');
  const sectionHeaderColor = isBatWhiteLabel ? clientPrimary : (isJtiWhiteLabel ? '#2f8f4e' : '#666');
  const subheaderColor = isBatWhiteLabel ? clientAccent : (isJtiWhiteLabel ? '#2f6d43' : '#555');
  const textColor = isBatWhiteLabel ? clientText : (isJtiWhiteLabel ? '#1f2d25' : '#333');
  const iconColor = isBatWhiteLabel ? clientPrimary : (isJtiWhiteLabel ? '#1f6f3f' : '#333');
  const activeBackground = isBatWhiteLabel ? clientActiveBackground : (isJtiWhiteLabel ? 'rgba(47, 143, 78, 0.14)' : 'rgba(25, 118, 210, 0.12)');
  const activeBorder = isBatWhiteLabel ? clientPrimary : (isJtiWhiteLabel ? '#2f8f4e' : '#1976d2');
  const hoverBackground = isBatWhiteLabel ? clientHoverBackground : (isJtiWhiteLabel ? 'rgba(47, 143, 78, 0.12)' : 'rgba(0, 0, 0, 0.08)');
  const footerBackground = isBatWhiteLabel ? clientLight : (isJtiWhiteLabel ? '#eef6f0' : '#f0f1f2');
  const footerTextColor = isBatWhiteLabel ? clientAccent : (isJtiWhiteLabel ? '#2f6d43' : '#666');
  const showBadges = false;

  const isItemAllowed = (itemId, item) => {
    if (!itemId || !item) return true;
    if (item.id === 'settings' && ['hq', 'market', 'old'].includes(normalizedScope)) {
      return true;
    }
    if (item.id === 'support' && ['hq', 'market', 'old'].includes(normalizedScope)) {
      return isModuleEnabled('support');
    }
    if (item.id === 'jti-export-data' && ['hq', 'market', 'old'].includes(normalizedScope) && ['jti', 'jti-global'].includes(projectKey)) {
      return true;
    }
    if (item.id === 'role-management') {
      return projectKey === 'admin' && isSuperAdmin;
    }
    if (!projectKey) {
      return ['settings', 'support'].includes(item.id);
    }
    if (projectKey === 'admin') {
      if (item.id === 'role-management') return isSuperAdmin;
      return ['settings', 'support'].includes(item.id);
    }
    if (projectKey === 'matching') {
      return ['settings', 'support', 'bat-product-matching', 'reference-catalog'].includes(item.id);
    }
    if (projectKey === 'sweden-snus-admin') {
      return ['settings', 'support', 'sweden-snus-admin', 'sweden-snus-reports'].includes(item.id);
    }
    if (projectKey === 'bat') {
      if (item.id === 'bat-product-matching') return isAdmin;
      return item.id === 'settings' || item.id === 'export-pricing-data' || isDistributionViewNavId(item.id) || isPriceMonitoringNavId(item.id);
    }
    if (projectKey === 'pmi') {
      if (item.id === 'bat-product-matching') return isAdmin;
      return item.id === 'settings' || item.id === 'export-pricing-data' || isDistributionViewNavId(item.id) || isPriceMonitoringNavId(item.id);
    }
    if (projectKey === 'asahi') {
      return item.id === 'settings' || isPriceMonitoringNavId(item.id);
    }
    if (projectKey === 'price_effects') {
      return item.id === 'settings' || item.id === 'bat-effects';
    }
    if (item.id === 'export-pricing-data') {
      return false;
    }
    if (item.id === 'sweden-snus-admin') {
      return projectKey === 'sweden-snus-admin';
    }
    if (item.id === 'jti-export-data' && !['jti', 'jti-global'].includes(projectKey)) {
      return false;
    }
    if (item.id === 'product-categorisation' && projectKey === 'jti-global') {
      return true;
    }
    if (item.id === 'jti-embedded-app') {
      return ['jti-global', 'jti'].includes(projectKey);
    }
    if (item.id === 'product-matching') {
      return false; // Replaced by jti-product-matching for JTI
    }
    if (item.id === 'jti-product-matching') {
      return ['jti-global', 'jti'].includes(projectKey);
    }
    if (item.id === 'product-availability') {
      return ['jti-global', 'jti'].includes(projectKey);
    }
    if (item.id === 'jti-products-overview') {
      return ['jti-global', 'jti'].includes(projectKey);
    }
    if (item.id === 'brand-board-summary-palette') {
      return isModuleEnabled('brand-board-summary');
    }
    // For real estate tabs: hide by default for non-real_estate_cz projects
    // But allow them if explicitly enabled via settings (even if hidden)
    if (item.id === 'real_estate' || item.id === 'real_estate_map') {
      // For real_estate_cz project, show if module is enabled (normal behavior)
      if (projectKey === 'real_estate_cz') {
        return isModuleEnabled(item.id);
      } else {
        // For other projects (jti, jti-sweden, etc.), hide by default
        // Only show if explicitly enabled via settings
        return isModuleEnabled(item.id);
      }
    }

    // For Lindt-only analytics: only show for lindt
    if (
      ['product', 'retailer', 'distribution_brand', 'distribution_retailer'].includes(item.id) &&
      projectKey !== 'lindt'
    ) {
      return false;
    }
    if (isDistributionViewNavId(item.id)) {
      return ['bat', 'pmi'].includes(projectKey);
    }

    // Price Monitoring: shared product_view (data source varies by project)
    if (isPriceMonitoringNavId(item.id) && !['bat', 'pmi', 'asahi', 'jti-sweden'].includes(projectKey)) {
      return false;
    }
    if (isPriceMonitoringNavId(item.id) && projectKey === 'jti-sweden') {
      return true;
    }
    if (item.id === 'bat-product-matching') {
      if (projectKey === 'matching') return true;
      return ['bat', 'pmi'].includes(projectKey) && isAdmin;
    }
    if (item.id === 'reference-catalog') {
      if (projectKey === 'matching') return true;
      return ['bat', 'pmi'].includes(projectKey) && isAdmin;
    }
    if (item.id === 'bat-effects') {
      return projectKey === 'price_effects';
    }

    // Total Ownership views are only for jti-sweden
    if (item.id === 'tocview' && projectKey !== 'jti-sweden') {
      return false;
    }
    
    // For all other items, use normal module filtering
    return isModuleEnabled(item.id);
  };

  // Filter navigation items based on enabled modules
  const navItems = allNavItems.filter(item => {
    return isItemAllowed(item.id, item);
  });

  // Group items by their group property
  const groupedItems = navItems.reduce((acc, item) => {
    const group = item.group || 'Other';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(item);
    return acc;
  }, {});

  const navItemById = allNavItems.reduce((acc, item) => {
    if (!acc[item.id]) {
      acc[item.id] = item;
    }
    return acc;
  }, {});

  const structuredSectionsByScope = {
    hq: [
      {
        title: 'Pricing',
        items: [
          { label: 'Devices', mapId: 'jti-product', pathSuffix: '/devices' },
          { label: 'Sticks', mapId: 'jti-product', pathSuffix: '/sticks' },
          {
            label: 'Accessories',
            mapId: 'jti-product',
            pathSuffix: '/accessories',
            requiresModule: 'accessories-category',
          },
        ]
      },
      {
        title: 'Brandboards',
        items: [
          { label: 'Devices', mapId: 'brand-board-summary-palette', pathSuffix: '/devices' },
          { label: 'Sticks', mapId: 'brand-board-summary-palette', pathSuffix: '/sticks' },
          {
            label: 'Accessories',
            mapId: 'brand-board-summary-palette',
            pathSuffix: '/accessories',
            requiresModule: 'accessories-category',
          },
        ]
      },
      {
        title: 'Availability',
        items: [
          { label: 'Product Availability', mapId: 'product-availability' }
        ]
      },
      {
        title: 'Mirror websites',
        items: [
          { label: 'Mirror websites', mapId: 'jti-embedded-app' }
        ]
      },
      {
        title: 'Banners',
        tag: 'in development',
        defaultCollapsed: true,
        items: [
          { label: 'Banner Evolution', mapId: 'banner-evolution' },
          
        ]
      },
      {
        title: 'Data & Tools',
        tag: 'in development',
        defaultCollapsed: true,
        items: [
          { label: 'Products Mapping Overview', mapId: 'jti-products-overview' },
          { label: 'Product Categorisation', mapId: 'product-categorisation' },
          { label: 'Pricing Types', mapId: 'pricing-types' },
          { label: 'Product Detail View', mapId: 'product-detail' },
          { label: 'Product Matching', mapId: 'jti-product-matching' },
          { label: 'Data Sources Overview', mapId: 'data-sources' },
          { label: 'Exports & Reports', mapId: 'jti-export-data' }
        ]
      },
      {
        title: 'Support & Settings',
        items: [
          { label: 'Support', mapId: 'support' },
          { label: 'Settings', mapId: 'settings' }
        ]
      }
    ],
    market: [
      {
        title: 'Pricing',
        items: [
          { label: 'Devices', mapId: 'jti-product', pathSuffix: '/devices' },
          { label: 'Sticks', mapId: 'jti-product', pathSuffix: '/sticks' },
          {
            label: 'Accessories',
            mapId: 'jti-product',
            pathSuffix: '/accessories',
            requiresModule: 'accessories-category',
          },
        ]
      },
      {
        title: 'Brandboards',
        items: [
          { label: 'Devices', mapId: 'brand-board-summary-palette', pathSuffix: '/devices' },
          { label: 'Sticks', mapId: 'brand-board-summary-palette', pathSuffix: '/sticks' },
          {
            label: 'Accessories',
            mapId: 'brand-board-summary-palette',
            pathSuffix: '/accessories',
            requiresModule: 'accessories-category',
          },
        ]
      },
      {
        title: 'Availability',
        items: [
          { label: 'Product Availability', mapId: 'product-availability' }
        ]
      },
      {
        title: 'Mirror websites',
        items: [
          { label: 'Mirror websites', mapId: 'jti-embedded-app' }
        ]
      },
      {
        title: 'Banners',
        tag: 'in development',
        defaultCollapsed: true,
        items: [
          { label: 'Banner Evolution', mapId: 'banner-evolution' },
        ]
      },
      {
        title: 'Data & Tools',
        tag: 'in development',
        defaultCollapsed: true,
        items: [
          { label: 'Products Mapping Overview', mapId: 'jti-products-overview' },
          { label: 'Product Detail View', mapId: 'product-detail' },
          { label: 'Product Matching', mapId: 'jti-product-matching' },
          { label: 'Exports & Reports', mapId: 'jti-export-data' }
        ]
      },
      {
        title: 'Support & Settings',
        items: [
          { label: 'Support', mapId: 'support' },
          { label: 'Settings', mapId: 'settings' }
        ]
      }
    ]
  };

  const structuredSections = structuredSectionsByScope[normalizedScope] || structuredSectionsByScope.hq;

  useEffect(() => {
    setExpandedSections((prev) => {
      const sections = structuredSectionsByScope[normalizedScope] || structuredSectionsByScope.hq;
      const next = { ...prev };
      sections.forEach((section) => {
        if (next[section.title] === undefined) {
          next[section.title] = section.defaultCollapsed ? false : true;
        }
      });
      return next;
    });
  }, [normalizedScope]);

  useEffect(() => {
    if (location.pathname.includes(`/${PRODUCT_VIEW_ROUTE_SEGMENT}/`)) {
      setPriceMonitoringExpanded(true);
    }
    if (location.pathname.includes(`/${DISTRIBUTION_VIEW_ROUTE_SEGMENT}/`)) {
      setDistributionViewExpanded(true);
    }
  }, [location.pathname]);

  const resolveStructuredItem = (itemConfig) => {
    if (itemConfig.requiresModule && !isModuleEnabled(itemConfig.requiresModule)) {
      return null;
    }
    if (itemConfig.type === 'subheader') {
      return {
        id: `subheader-${itemConfig.label}`,
        label: itemConfig.label,
        isSubheader: true,
        disabled: true
      };
    }

    if (itemConfig.placeholder) {
      return {
        id: `placeholder-${itemConfig.label}`,
        label: itemConfig.label,
        icon: <DataObject />,
        badge: { text: 'soon', color: '#9e9e9e' },
        path: null,
        disabled: true,
        indent: itemConfig.indent
      };
    }

    const baseItem = navItemById[itemConfig.mapId];
    if (!baseItem) {
      return {
        id: `missing-${itemConfig.label}`,
        label: itemConfig.label,
        icon: <Assessment />,
        badge: { text: 'soon', color: '#9e9e9e' },
        path: null,
        disabled: true
      };
    }

    if (!isItemAllowed(baseItem.id, baseItem)) {
      return null;
    }

    const resolvedPath = itemConfig.path
      ? itemConfig.path
      : itemConfig.pathSuffix
        ? `${baseItem.path}${itemConfig.pathSuffix}`
        : baseItem.path;

    return {
      ...baseItem,
      label: itemConfig.label,
      path: resolvedPath,
      disabled: false,
      indent: itemConfig.indent
    };
  };

  const toggleSection = (sectionTitle) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  const renderStructuredNav = () => (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '16px 0' }}>
      {structuredSections.map((section, sectionIndex) => {
        const sectionItems = section.items
          .map(resolveStructuredItem)
          .filter(Boolean);
        const isExpanded = expandedSections[section.title] ?? true;

        if (sectionItems.length === 0) {
          return null;
        }

        return (
          <Box key={section.title}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px 4px 16px',
                gap: 1
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: sectionHeaderColor,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  flex: 1
                }}
              >
                {section.title}
                {section.tag && (
                  <Typography
                    component="span"
                    sx={{
                      color: sectionHeaderColor,
                      fontSize: '0.6rem',
                      fontWeight: 500,
                      fontStyle: 'italic',
                      letterSpacing: 0,
                      textTransform: 'none',
                      marginLeft: '6px',
                      opacity: 0.8
                    }}
                  >
                    ({section.tag})
                  </Typography>
                )}
              </Typography>
              <IconButton
                size="small"
                onClick={() => toggleSection(section.title)}
                sx={{ color: sectionHeaderColor }}
              >
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List sx={{ padding: 0 }}>
                {sectionItems.map((item, index) => {
                  if (item.isSubheader) {
                    return (
                      <ListItem
                        key={`${section.title}-sub-${index}`}
                        component="div"
                        sx={{
                          padding: '8px 16px 4px 20px',
                          margin: '6px 8px 2px',
                          borderRadius: '6px'
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: subheaderColor, textTransform: 'uppercase' }}
                        >
                          {item.label}
                        </Typography>
                      </ListItem>
                    );
                  }

                  const effectivePath = (item.id === 'bat-effects' && projectKey === 'bat') ? '/price_effects/bat-effects' : item.path;
                  const isActive = effectivePath ? location.pathname === effectivePath : false;
                  const listItemProps = effectivePath
                    ? { component: Link, to: effectivePath }
                    : { component: 'div' };

                  return (
                    <ListItem
                      key={`${section.title}-${index}`}
                      {...listItemProps}
                      sx={{
                        padding: '8px 16px',
                        paddingLeft: item.indent ? '32px' : '16px',
                        margin: '2px 8px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: isActive ? activeBackground : 'transparent',
                        borderLeft: isActive ? `4px solid ${activeBorder}` : '4px solid transparent',
                        opacity: item.disabled ? 0.6 : 1,
                        pointerEvents: item.disabled ? 'none' : 'auto',
                        '&:hover': item.disabled ? {} : {
                          backgroundColor: isActive ? activeBackground : hoverBackground,
                          transform: 'translateX(4px)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: iconColor, 
                        minWidth: 40,
                        marginRight: '12px'
                      }}>
                        {item.icon}
                      </ListItemIcon>

                      <ListItemText
                        sx={{ minWidth: 0, flex: 1 }}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                color: textColor,
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {item.label}
                            </Typography>
                            {showBadges && item.badge && (
                              <Box
                                sx={{
                                  marginLeft: '4px',
                                  flexShrink: 0,
                                  fontSize: '0.6rem',
                                  height: 18,
                                  minWidth: 18,
                                  borderRadius: '12px',
                                  padding: '0 6px',
                                  backgroundColor: item.badge.color,
                                  color: 'white',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  lineHeight: 1
                                }}
                              >
                                {item.badge.text}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>

            {sectionIndex < structuredSections.length - 1 && (
              <Divider sx={{ margin: '6px 16px' }} />
            )}
          </Box>
        );
      })}
    </Box>
  );

  const buildLabel = `Latest app update: ${appVersion || '-'} ${appVersionDate || '-'}`;

  const renderGroupedNavLink = (item, key, { nested = false } = {}) => {
    const effectivePath = (item.id === 'bat-effects' && projectKey === 'bat') ? '/price_effects/bat-effects' : item.path;
    const isActive = location.pathname === effectivePath;
    return (
      <ListItem
        key={key}
        component={Link}
        to={effectivePath}
        sx={{
          padding: nested ? '8px 16px 8px 28px' : '8px 16px',
          margin: '2px 8px',
          borderRadius: '8px',
          transition: 'all 0.2s ease-in-out',
          backgroundColor: isActive ? activeBackground : 'transparent',
          borderLeft: isActive ? `4px solid ${activeBorder}` : '4px solid transparent',
          '&:hover': {
            backgroundColor: isActive ? activeBackground : hoverBackground,
            transform: 'translateX(4px)',
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: iconColor,
            minWidth: 40,
            marginRight: '12px',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          sx={{ minWidth: 0, flex: 1 }}
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: textColor,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
              {showBadges && item.badge && (
                <Box
                  sx={{
                    marginLeft: '4px',
                    flexShrink: 0,
                    fontSize: '0.6rem',
                    height: 18,
                    minWidth: 18,
                    borderRadius: '12px',
                    padding: '0 6px',
                    backgroundColor: item.badge.color,
                    color: 'white',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  {item.badge.text}
                </Box>
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      paddingTop: '56px', // Add top padding to account for fixed navbar
      boxSizing: 'border-box',
      minHeight: 0,
      overflow: 'hidden'
    }}>

      {/* Navigation Items */}
      {isJtiScopeProject && normalizedScope !== 'old' ? (
        renderStructuredNav()
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '16px 0' }}>
          {Object.entries(groupedItems).map(([groupName, items], groupIndex) => {
            const priceMonitoringItems = items
              .filter((i) => isPriceMonitoringNavId(i.id))
              .sort((a, b) => PRICE_MONITORING_NAV_IDS.indexOf(a.id) - PRICE_MONITORING_NAV_IDS.indexOf(b.id));
            const distributionViewItems = items
              .filter((i) => isDistributionViewNavId(i.id))
              .sort((a, b) => DISTRIBUTION_VIEW_NAV_IDS.indexOf(a.id) - DISTRIBUTION_VIEW_NAV_IDS.indexOf(b.id));
            const regularItems = items.filter((i) => !isPriceMonitoringNavId(i.id) && !isDistributionViewNavId(i.id));
            const regularItemsBeforePm = regularItems.filter((i) => afterPriceMonitoringOrderIndex(i.id) === -1);
            const regularItemsAfterPm = regularItems
              .filter((i) => afterPriceMonitoringOrderIndex(i.id) !== -1)
              .sort((a, b) => afterPriceMonitoringOrderIndex(a.id) - afterPriceMonitoringOrderIndex(b.id));
            const isPriceMonitoringSectionActive =
              priceMonitoringItems.length > 0 &&
              priceMonitoringItems.some((item) => {
                const ep = item.path;
                return ep && location.pathname === ep;
              });
            const isDistributionViewSectionActive =
              distributionViewItems.length > 0 &&
              distributionViewItems.some((item) => {
                const ep = item.path;
                return ep && location.pathname === ep;
              });

            return (
            <Box key={groupName}>
              {/* Group Header */}
              <Typography
                variant="overline"
                sx={{
                  color: sectionHeaderColor,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  padding: '16px 16px 8px 16px',
                  textTransform: 'uppercase'
                }}
              >
                {groupName}
              </Typography>
              
              <List sx={{ padding: 0 }}>
                {regularItemsBeforePm.map((item, index) =>
                  renderGroupedNavLink(item, `${groupName}-reg-${index}`, { nested: Boolean(item.nested) })
                )}
                {priceMonitoringItems.length > 0 && (
                  <>
                    <ListItemButton
                      onClick={() => setPriceMonitoringExpanded((open) => !open)}
                      sx={{
                        padding: '8px 16px',
                        margin: '2px 8px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: isPriceMonitoringSectionActive ? activeBackground : 'transparent',
                        borderLeft: isPriceMonitoringSectionActive ? `4px solid ${activeBorder}` : '4px solid transparent',
                        '&:hover': {
                          backgroundColor: isPriceMonitoringSectionActive ? activeBackground : hoverBackground,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: iconColor, minWidth: 40, marginRight: '12px' }}>
                        <Analytics />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: textColor }}>
                            Price Monitoring
                          </Typography>
                        }
                      />
                      {priceMonitoringExpanded ? (
                        <ExpandLess sx={{ color: iconColor }} />
                      ) : (
                        <ExpandMore sx={{ color: iconColor }} />
                      )}
                    </ListItemButton>
                    <Collapse in={priceMonitoringExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {priceMonitoringItems.map((item, index) =>
                          renderGroupedNavLink(item, `${groupName}-pm-${index}`, { nested: true })
                        )}
                      </List>
                    </Collapse>
                  </>
                )}
                {distributionViewItems.length > 0 && (
                  <>
                    <ListItemButton
                      onClick={() => setDistributionViewExpanded((open) => !open)}
                      sx={{
                        padding: '8px 16px',
                        margin: '2px 8px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: isDistributionViewSectionActive ? activeBackground : 'transparent',
                        borderLeft: isDistributionViewSectionActive ? `4px solid ${activeBorder}` : '4px solid transparent',
                        '&:hover': {
                          backgroundColor: isDistributionViewSectionActive ? activeBackground : hoverBackground,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: iconColor, minWidth: 40, marginRight: '12px' }}>
                        <Assessment />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: textColor }}>
                            Distribution View
                          </Typography>
                        }
                      />
                      {distributionViewExpanded ? (
                        <ExpandLess sx={{ color: iconColor }} />
                      ) : (
                        <ExpandMore sx={{ color: iconColor }} />
                      )}
                    </ListItemButton>
                    <Collapse in={distributionViewExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {distributionViewItems.map((item, index) =>
                          renderGroupedNavLink(item, `${groupName}-dv-${index}`, { nested: true })
                        )}
                      </List>
                    </Collapse>
                  </>
                )}
                {regularItemsAfterPm.map((item, index) =>
                  renderGroupedNavLink(item, `${groupName}-afterpm-${index}`, { nested: Boolean(item.nested) })
                )}
              </List>
              
              {/* Add divider between groups (except for the last group) */}
              {groupIndex < Object.keys(groupedItems).length - 1 && (
                <Divider sx={{ margin: '16px 16px' }} />
              )}
            </Box>
            );
          })}
        </Box>
      )}
      
      {/* API Version at bottom */}
      <Box
        sx={{
          mt: 'auto',
          flexShrink: 0,
          padding: '8px 16px',
          borderTop: `1px solid ${sidebarBorder}`,
          backgroundColor: footerBackground
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: footerTextColor,
            fontSize: '0.6rem',
            textAlign: 'center',
            display: 'block',
            fontWeight: 500
          }}
        >
          Forloop Digishelf Insights - API {apiVersion}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: footerTextColor,
            fontSize: '0.5rem',
            textAlign: 'center',
            display: 'block',
            fontWeight: 400
          }}
        >
          {buildLabel}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={() => setMobileOpen(false)}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        zIndex: 1200,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: sidebarBackground,
          color: textColor,
          borderRight: `1px solid ${sidebarBorder}`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          overflowX: 'hidden',
          zIndex: 1200
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;